import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DialogService } from '@gains-com/ui-angular';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  CRM_POPUP_DATA,
  CrmPopupAction,
  CrmPopupComponent,
  CrmPopupViewData,
} from './crm-popup.component.js';
import { PendingCrmPopup, SelfApiService } from '../../core/self-api.service.js';
import { DialogKey } from '../../shared/dialog-keys.js';

/** Minimum gap between two popups so back-to-back sends don't chain-interrupt. */
const POPUP_COOLDOWN_MS = 30_000;

/**
 * CRM popup channel, client side. Pulls the user's pending popups (session
 * start + on the thin `crm_popup` realtime signal), queues them, and shows
 * them one at a time in the server's priority order: `banner` in a raw
 * top-center CDK overlay (the VIP level-up toast precedent), `modal` through
 * `DialogService`. Acks (shown / clicked / dismissed) are fire-and-forget.
 *
 * The queue holds while a purchases / KYC / document-verification dialog is
 * open — a promo must never interrupt money or identity flows — and resumes
 * when they close. No-ops on the server (SSR).
 */
@Injectable({ providedIn: 'root' })
export class CrmPopupNotificationService {
  private readonly _overlay = inject(Overlay);
  private readonly _injector = inject(Injector);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _dialog = inject(DialogService);
  private readonly _selfApi = inject(SelfApiService);
  private readonly _router = inject(Router);

  private readonly _queue: PendingCrmPopup[] = [];
  /** Every token ever queued this session — dedupes re-pulls of not-yet-acked popups. */
  private readonly _seenTokens = new Set<string>();
  private _draining = false;
  private _fetching = false;
  private _refetchQueued = false;
  private _lastClosedAt = 0;

  /**
   * Pulls pending popups and starts (or feeds) the one-at-a-time display
   * queue. Concurrent calls coalesce: while a pull is in flight, further calls
   * collapse into a single follow-up pull.
   */
  async fetchAndShow(): Promise<void> {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    if (this._fetching) {
      this._refetchQueued = true;
      return;
    }

    this._fetching = true;
    try {
      const { popups } = await this._selfApi.getPendingCrmPopups();
      const now = Date.now();
      for (const popup of popups) {
        if (Date.parse(popup.expires_at) <= now || this._seenTokens.has(popup.dispatch_token)) {
          continue;
        }
        this._seenTokens.add(popup.dispatch_token);
        this._queue.push(popup);
      }
      // Keep the server's order (priority desc, soonest-expiring first) when a
      // realtime-triggered re-pull appends to an already-populated queue.
      this._queue.sort(
        (a, b) => b.priority - a.priority || a.expires_at.localeCompare(b.expires_at),
      );
    } catch {
      // Background pull — a failure surfaces nothing; the next signal retries.
    } finally {
      this._fetching = false;
      if (this._refetchQueued) {
        this._refetchQueued = false;
        void this.fetchAndShow();
      }
    }

    void this._drain();
  }

  private async _drain(): Promise<void> {
    if (this._draining) {
      return;
    }
    this._draining = true;
    try {
      while (this._queue.length > 0) {
        await this._cooldown();
        await this._untilNotSuppressed();

        const popup = this._queue.shift()!;
        // Re-check expiry — the popup may have aged out waiting on the
        // cooldown or a suppressing dialog.
        if (Date.parse(popup.expires_at) <= Date.now()) {
          continue;
        }

        // The shown ack is what retires a popup server-side, so it rides the
        // popup document reporting itself rendered — not the host being
        // constructed, and certainly not this call site. A popup that never
        // renders is never acked, so it stays pending: the server lease
        // lapses and a later pull hands it back. Dropping the token from
        // `_seenTokens` is what lets this session's own later pull re-queue
        // it, rather than deduping it away for the rest of the session.
        let action: CrmPopupAction;
        try {
          action = await this._show(popup, () =>
            this._selfApi.reportCrmPopupShown(popup.dispatch_token),
          );
        } catch {
          this._seenTokens.delete(popup.dispatch_token);
          continue;
        }
        this._lastClosedAt = Date.now();

        if (action === 'cta') {
          void this._selfApi.reportCrmPopupClicked(popup.dispatch_token);
          if (popup.deep_link) {
            this._navigate(popup.deep_link);
          }
        } else {
          void this._selfApi.reportCrmPopupDismissed(popup.dispatch_token);
        }
      }
    } finally {
      this._draining = false;
    }
  }

  private async _cooldown(): Promise<void> {
    if (this._lastClosedAt === 0) {
      return;
    }
    const remaining = POPUP_COOLDOWN_MS - (Date.now() - this._lastClosedAt);
    if (remaining > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, remaining));
    }
  }

  /** Auth / money / identity flows outrank promos — hold until all of them close. */
  private async _untilNotSuppressed(): Promise<void> {
    for (;;) {
      const openRefs = [
        DialogKey.Auth,
        DialogKey.Purchases,
        DialogKey.Shop,
        DialogKey.KycVerification,
        DialogKey.DocumentVerification,
      ]
        .map((id) => this._dialog.getDialogRefById(id))
        .filter((ref) => !!ref);
      if (openRefs.length === 0) {
        return;
      }
      await Promise.race(openRefs.map((ref) => firstValueFrom(ref!.closed)));
    }
  }

  /**
   * Presents one popup and resolves with its outcome. `onPresented` is handed
   * to the popup component, which fires it when the document first speaks over
   * its bridge — so a host that throws on creation, or an iframe that never
   * renders, reports nothing at all.
   */
  private _show(popup: PendingCrmPopup, onPresented: () => void): Promise<CrmPopupAction> {
    return popup.style === 'banner'
      ? this._showBanner(popup, onPresented)
      : this._showModal(popup, onPresented);
  }

  private _showModal(popup: PendingCrmPopup, onPresented: () => void): Promise<CrmPopupAction> {
    return new Promise<CrmPopupAction>((resolve) => {
      let action: CrmPopupAction | null = null;
      const data: CrmPopupViewData = {
        html: popup.html,
        style: popup.style,
        onPresented,
        onAction: (a) => {
          action ??= a;
          ref.close();
        },
      };
      const ref = this._dialog.open<void, CrmPopupViewData, CrmPopupComponent>(
        CrmPopupComponent,
        { data, panelClass: 'crm-popup-dialog-panel' },
      );
      // Backdrop / ESC close counts as a dismissal.
      ref.closed.pipe(take(1)).subscribe(() => resolve(action ?? 'dismiss'));
    });
  }

  private _showBanner(popup: PendingCrmPopup, onPresented: () => void): Promise<CrmPopupAction> {
    return new Promise<CrmPopupAction>((resolve) => {
      let action: CrmPopupAction | null = null;
      const overlayRef = this._overlay.create({
        positionStrategy: this._overlay.position().global().top('16px').centerHorizontally(),
        hasBackdrop: false,
        panelClass: 'crm-popup-banner-overlay',
        scrollStrategy: this._overlay.scrollStrategies.reposition(),
      });

      const data: CrmPopupViewData = {
        html: popup.html,
        style: popup.style,
        onPresented,
        onAction: (a) => {
          action ??= a;
          overlayRef.dispose();
        },
      };
      const injector = Injector.create({
        parent: this._injector,
        providers: [{ provide: CRM_POPUP_DATA, useValue: data }],
      });

      overlayRef.attach(new ComponentPortal(CrmPopupComponent, null, injector));
      overlayRef
        .detachments()
        .pipe(take(1))
        .subscribe(() => resolve(action ?? 'dismiss'));
    });
  }

  private _navigate(deepLink: string): void {
    // CRM deep links are in-app paths; an absolute URL opens a new tab so the
    // session (and any remaining queued popups) isn't torn down.
    if (/^https?:\/\//i.test(deepLink)) {
      window.open(deepLink, '_blank', 'noopener');
    } else {
      void this._router.navigateByUrl(deepLink);
    }
  }
}
